import { useEffect } from "react";
import { createPortal } from "react-dom";

import type { JSX, ReactNode } from "react";

type ActionProps = {
  label: string;
  onClick: (
    event: React.UIEvent<HTMLButtonElement, MouseEvent> | KeyboardEvent,
  ) => void;
  buttonStyle?: string;
  showLoadingIndicator?: boolean;
};

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  primaryAction: ActionProps;
  secondaryAction?: ActionProps;
  spreadButtons?: boolean;
  className?: string;
};

export const Modal = ({
  isOpen,
  onClose,
  title = "Modal Title",
  children,
  primaryAction,
  secondaryAction,
  spreadButtons,
  className,
}: ModalProps): JSX.Element | null => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Enter") {
        primaryAction?.onClick(e);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return (): void => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [primaryAction]);

  return isOpen
    ? createPortal(
        <>
          <div
            className={`modal show ${className}`}
            style={{ display: "block" }}
          >
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{title}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={onClose}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">{children}</div>
                <div
                  className={
                    spreadButtons
                      ? "modal-footer d-flex justify-content-between"
                      : "modal-footer"
                  }
                >
                  {secondaryAction && (
                    <button
                      type="button"
                      className={`btn btn-${secondaryAction.buttonStyle || "secondary"}`}
                      onClick={secondaryAction.onClick}
                    >
                      {secondaryAction.showLoadingIndicator ? (
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          aria-hidden="true"
                        ></span>
                      ) : null}
                      {secondaryAction.label}
                    </button>
                  )}
                  {primaryAction && (
                    <button
                      type="button"
                      className={`btn btn-${primaryAction.buttonStyle || "primary"}`}
                      onClick={primaryAction.onClick}
                    >
                      {primaryAction.showLoadingIndicator ? (
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          aria-hidden="true"
                        ></span>
                      ) : null}
                      {primaryAction.label}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" />
        </>,
        document.body,
      )
    : null;
};
